import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", message: "Confirming payment…" });
  const { clear } = useCart();
  const clearRef = useRef(clear);
  clearRef.current = clear;
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }

    let cancelled = false;
    const MAX = 8;
    const INTERVAL = 2000;

    async function poll() {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await apiClient.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearRef.current();
          setState({ status: "success", message: "Payment received", order: data.order_id });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", message: "Checkout session expired. Please try again." });
          return;
        }
        if (attemptsRef.current >= MAX) {
          setState({ status: "pending", message: `Payment still processing. We'll email you when it confirms.` });
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch {
        if (attemptsRef.current >= MAX) {
          setState({ status: "error", message: "Could not verify payment. Contact support." });
          return;
        }
        setTimeout(poll, INTERVAL);
      }
    }

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="max-w-[900px] mx-auto px-6 py-24 text-center" data-testid="checkout-success-page">
      {state.status === "polling" && (
        <div className="flex flex-col items-center" data-testid="status-polling">
          <Loader2 className="w-12 h-12 text-[#c8102e] animate-spin" />
          <h1 className="font-display text-5xl uppercase tracking-tight mt-6">Hold tight…</h1>
          <p className="text-[#9ca3af] mt-3">{state.message}</p>
        </div>
      )}
      {state.status === "success" && (
        <div data-testid="status-success">
          <CheckCircle2 className="w-16 h-16 text-[#22c55e] mx-auto" />
          <div className="bgc-eyebrow text-[#c8102e] mt-6">ORDER CONFIRMED</div>
          <h1 className="font-display text-6xl uppercase tracking-tight mt-3">{`You're In.`}</h1>
          <p className="text-[#d1d5db] mt-4 max-w-lg mx-auto">
            {`Thanks for riding with us. We'll send a tracking number as soon as your order ships from the garage.`}
          </p>
          {state.order && (
            <p className="text-xs text-[#6b7280] mt-4">Order ID: {state.order}</p>
          )}
          <Link to="/shop" className="btn-primary mt-10 inline-flex" data-testid="success-continue">
            KEEP SHOPPING
          </Link>
        </div>
      )}
      {state.status === "pending" && (
        <div data-testid="status-pending">
          <Loader2 className="w-12 h-12 text-[#c8102e] mx-auto" />
          <h1 className="font-display text-5xl uppercase tracking-tight mt-6">Processing…</h1>
          <p className="text-[#9ca3af] mt-3">{state.message}</p>
        </div>
      )}
      {state.status === "error" && (
        <div data-testid="status-error">
          <XCircle className="w-16 h-16 text-[#c8102e] mx-auto" />
          <h1 className="font-display text-5xl uppercase tracking-tight mt-6">Something went wrong</h1>
          <p className="text-[#9ca3af] mt-3">{state.message}</p>
          <Link to="/cart" className="btn-ghost mt-10 inline-flex" data-testid="error-back-cart">
            BACK TO CART
          </Link>
        </div>
      )}
    </div>
  );
}
